import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Pagination,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import nodatafound from '../../utilities/dummy_assets/nodatafound.jpg';
import EquipmentListAPIs from '../../utilities/api/equipment/AdminEquipmentList';
import EquipmentDetailAPIs from '../../utilities/api/equipment/AdminEquipmentDetail';
import Delete from './Popups/Delete';
import EditEquipmentDialog from './Popups/EditEquipment';
import { ImageBaseURL } from '../../utilities/api/axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomPagination from '../CustomPagination/CustomPagination';
import TuneIcon from '@mui/icons-material/Tune';
import CommonInventoryCard from './CommonInventoryCard';
import { dataGridSx } from './MachineTable';

/* ------- Card Fields -----*/

const equipmentFields = [
  { label: 'Category', value: 'category', type: 'chip' },
  { label: 'Status', value: 'status' },
  { label: 'Availability', value: 'availability' },
  { label: 'Supervised', value: r => (r.supervised ? 'Yes' : 'No') },
  { label: 'Manufacturer', value: 'manufacturer' },
  { label: 'Location', value: 'location', type: 'tooltip' },
  { label: 'Description', value: 'description', type: 'tooltip' },
  { label: 'Stock', value: 'instances' },
];

/* ---------- DataGrid Columns ---------- */

const columns = (handleEditInventory, handleOpenDeletePopup) => [
  {
    field: 'name',
    headerName: 'Equipment Name',
    minWidth: 200,
    flex: 1.5,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => {
      const imageUrl =
        params.row && params.row.image
          ? params.row.image.startsWith('http')
            ? params.row.image
            : `${ImageBaseURL}${params.row.image}`
          : nodatafound;
      const text = params.row && params.row.name ? params.row.name : 'No Text';
      const subtext =
        params.row && params.row.upc ? params.row.upc : 'No Subtext';

      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <img
            src={imageUrl}
            alt={text}
            style={{ width: '50px', height: '50px', borderRadius: '50%' }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#434343' }}>
              {text}
            </Typography>
            <Typography variant="body2" sx={{ color: '#8C8C8C' }}>
              {subtext}
            </Typography>
          </Box>
        </Box>
      );
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box
        sx={{
          border: '1px solid #E6E6E6',
          borderRadius: '50px',
          padding: '4px 8px',
        }}
      >
        <Typography sx={{ color: '#8C8C8C', textAlign: 'left', width: '100%' }}>
          {params.row.category}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.status}
      </Typography>
    ),
  },
  {
    field: 'availability',
    headerName: 'Availability',
    width: 120,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.availability || '-'}
      </Typography>
    ),
  },
  {
    field: 'supervised',
    headerName: 'Supervised',
    width: 120,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.supervised ? 'Yes' : 'No'}
      </Typography>
    ),
  },
  {
    field: 'manufacturer',
    headerName: 'Manufacturer',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.manufacturer}
      </Typography>
    ),
  },
  {
    field: 'location',
    headerName: 'Location',
    minWidth: 150,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography
        sx={{
          color: '#434343',
          textAlign: 'left',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={params.row.location || '-'}
      >
        {params.row.location || '-'}
      </Typography>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 200,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography
        sx={{
          color: '#434343',
          textAlign: 'left',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={params.row.description || '-'}
      >
        {params.row.description}
      </Typography>
    ),
  },
  {
    field: 'instances',
    headerName: 'Stock',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.instances}
      </Typography>
    ),
  },
  {
    field: 'action',
    headerName: 'Action',
    sortable: false,
    width: 150,
    headerAlign: 'left',
    align: 'center',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <IconButton
          aria-label="edit"
          onClick={() => handleEditInventory(params.row)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="delete"
          onClick={() => handleOpenDeletePopup(params.row)}
        >
          <DeleteOutlineSharpIcon fontSize="small" sx={{ color: '#C90505' }} />
        </IconButton>
      </Box>
    ),
  },
];

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <img
      src={nodatafound}
      alt="No Data Found"
      style={{ width: '250px', marginBottom: '8px' }}
    />
    <Typography variant="body1" color="textSecondary">
      No data available
    </Typography>
  </Box>
);

const CustomLoadingOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <CircularProgress size={40} />
    <Typography sx={{ mt: 2 }} color="textSecondary">
      Updating list...
    </Typography>
  </Box>
);

const EquipmentsTable = ({
  searchQuery,
  category,
  update,
  setUpdate,
  flag,
  setFlag,
  setTableDataProp,
  status,
  supervised,
}) => {
  const user = useSelector(state => state.user.user);
  const token = user.access;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [isEditInventoryOpen, setEditInventoryOpen] = useState(false);

  // ✅ 1. Reset page to 0 whenever search query or category changes
  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchQuery, category, status, supervised]);

  // ✅ 2. Fetch data from server based on pagination, search, and category
  useEffect(() => {
    setLoading(true);

    EquipmentListAPIs.EquipmentListGet({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: searchQuery,
      category: category,
      status: status,
      supervised: supervised,
    })
      .then(result => {
        // Backend returns { count, next, previous, results }
        const data = result.results || [];
        setTableData(data);
        setRowCount(result.count || 0);
        if (setTableDataProp) setTableDataProp(data);
      })
      .catch(() => toast.error('Error fetching equipment data'))
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    searchQuery,
    category,
    status,
    supervised,
    update,
    user,
  ]);

  // Rows for DataGrid (No longer filtering locally)
  const rows = tableData.map((item, index) => ({ ...item, index }));

  const handleEditInventory = row => {
    setCurrentRow(row);
    setEditInventoryOpen(true);
  };

  const handleOpenDeletePopup = row => {
    setCurrentRow(row);
    setOpenDeletePopup(true);
  };

  const handleCloseDeletePopup = () => setOpenDeletePopup(false);

  const handleDelete = () => {
    EquipmentDetailAPIs.EquipmentDetailDelete(currentRow.id, token)
      .then(() => {
        setUpdate(prev => !prev);
        toast.success('Item successfully deleted');
      })
      .catch(() => toast.error('Error deleting item'));

    handleCloseDeletePopup();
  };

  /* ---------- Card Pagination ---------- */
  const cardsPerPage = paginationModel.pageSize;
  const totalPages = Math.ceil(rowCount / cardsPerPage);

  return (
    <Box
      sx={{
        width: '100%',
        background: '#fff',
        borderRadius: '12px',
        p: 2,
        minHeight: '550px',
      }}
    >
      {isMobile || isTablet ? (
        /* ---------- MOBILE/TABLET CARDS ---------- */
        <Box position="relative" sx={{ minHeight: '400px' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                minHeight: '200px',
              }}
            >
              <CircularProgress size={40} />
              <Typography mt={2}>Updating list...</Typography>
            </Box>
          )}

          {rows.length === 0 && !loading ? (
            <Box
              textAlign="center"
              py={10}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img src={nodatafound} alt="no data found" width="250" />
              <Typography color="textSecondary" mt={1}>
                No data available
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {rows.map(row => (
                  <Grid item xs={12} sm={6} key={row.id}>
                    <CommonInventoryCard
                      row={row}
                      fields={equipmentFields}
                      onEdit={handleEditInventory}
                      onDelete={handleOpenDeletePopup}
                    />
                  </Grid>
                ))}
              </Grid>

              <Stack alignItems="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={paginationModel.page + 1}
                  onChange={(e, value) =>
                    setPaginationModel(prev => ({ ...prev, page: value - 1 }))
                  }
                  color="primary"
                />
              </Stack>
            </>
          )}
        </Box>
      ) : (
        /* ---------- DESKTOP TABLE ---------- */
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={rows}
            rowHeight={85}
            columns={columns(handleEditInventory, handleOpenDeletePopup)}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[7, 10, 25]}
            slots={{
              pagination: CustomPagination,
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: CustomLoadingOverlay,
              columnMenuIcon: () => (
                <TuneIcon
                  sx={{
                    transform: 'rotate(90deg)',
                    fill: '#8C8C8C',
                    height: 20,
                    width: 18,
                  }}
                />
              ),
            }}
            disableDensitySelector
            disableExport
            disableMultipleColumnsSorting
            sortingOrder={[]}
            disableColumnResize
            sx={dataGridSx}
          />
        </Box>
      )}

      <Delete
        open={openDeletePopup}
        onClose={handleCloseDeletePopup}
        onConfirm={handleDelete}
      />

      <EditEquipmentDialog
        currentRow={currentRow}
        open={isEditInventoryOpen}
        onClose={() => setEditInventoryOpen(false)}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
      />

      <ToastContainer />
    </Box>
  );
};

export default EquipmentsTable;
